import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Gift, CheckCircle, EnvelopeSimple, XCircle } from "@phosphor-icons/react";

const PRESETS = [7, 30, 90, 365];

export default function GiftProManager() {
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [gifts, setGifts] = useState([]);

  const loadGifts = async () => {
    try {
      const { data } = await api.get("/admin/gifts?limit=20");
      setGifts(data.gifts || []);
    } catch {}
  };

  useEffect(() => { loadGifts(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !days || sending) return;
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await api.post("/admin/gift-pro", {
        email: email.trim(),
        days: Number(days),
        note: note.trim() || undefined,
        notify,
      });
      const newUntil = new Date(data.gift.new_pro_until).toLocaleDateString("es-ES", { dateStyle: "long" });
      setFeedback({
        ok: true,
        text: `Pro activo hasta ${newUntil}` + (data.gift.email_sent ? " · email enviado" : (notify ? " · email no enviado (revisa Resend)" : "")),
      });
      setEmail("");
      setNote("");
      loadGifts();
    } catch (err) {
      setFeedback({ ok: false, text: err.response?.data?.detail || "Error al regalar Pro" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6"
      data-testid="gift-pro-manager"
    >
      <div className="flex items-center justify-between mb-5">
        <p className="eyebrow flex items-center gap-2">
          <Gift size={16} className="text-[hsl(var(--primary))]" />
          regalar pro
        </p>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">
          Para influencers, devoluciones, beta testers…
        </span>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_auto] gap-3 items-start">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Email del usuario</label>
          <input
            type="email"
            required
            data-testid="gift-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="influencer@ejemplo.com"
            className="mt-1 w-full px-4 py-2.5 bg-[hsl(var(--background))] rounded-full border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Días Pro</label>
          <input
            type="number"
            required
            min={1}
            max={3650}
            data-testid="gift-days"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="mt-1 w-full px-4 py-2.5 bg-[hsl(var(--background))] rounded-full border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>
        <button
          type="submit"
          disabled={sending}
          data-testid="gift-submit"
          className="self-end md:mt-[22px] px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold btn-press disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Gift size={16} weight="fill" /> {sending ? "Regalando…" : "Regalar Pro"}
        </button>

        <div className="md:col-span-3 flex flex-wrap items-center gap-2">
          {PRESETS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`gift-preset-${d}`}
              className={`text-xs px-3 py-1 rounded-full border ${days == d ? "bg-[hsl(var(--foreground))] text-white border-[hsl(var(--foreground))]" : "bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
            >
              {d} días
            </button>
          ))}
          <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <input
              type="checkbox"
              checked={notify}
              data-testid="gift-notify"
              onChange={(e) => setNotify(e.target.checked)}
              className="accent-[hsl(var(--primary))]"
            />
            Avisar por email al usuario
          </label>
        </div>

        <div className="md:col-span-3">
          <input
            type="text"
            data-testid="gift-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota interna o mensaje opcional (sale en el email si está activado)"
            maxLength={300}
            className="w-full px-4 py-2.5 bg-[hsl(var(--background))] rounded-full border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>
      </form>

      {feedback && (
        <div
          data-testid="gift-feedback"
          className={`mt-3 text-sm inline-flex items-center gap-1.5 ${feedback.ok ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--destructive))]"}`}
        >
          {feedback.ok ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
          {feedback.text}
        </div>
      )}

      {/* Recent gifts */}
      <div className="mt-6">
        <p className="eyebrow mb-3 text-[11px]">últimos regalos</p>
        {gifts.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Aún no has regalado Pro a nadie.</p>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]" data-testid="gifts-list">
            {gifts.map((g) => (
              <div key={g.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{g.email}</p>
                  {g.note && <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{g.note}</p>}
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                    {g.days} días
                  </span>
                  <span className="text-[hsl(var(--muted-foreground))]">
                    hasta {new Date(g.new_pro_until).toLocaleDateString("es-ES", { dateStyle: "short" })}
                  </span>
                  <span
                    title={g.email_sent ? "Email enviado al usuario" : "No se notificó por email"}
                    className={g.email_sent ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--muted-foreground))]"}
                  >
                    <EnvelopeSimple size={14} weight={g.email_sent ? "fill" : "regular"} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
